import { useState, type FormEvent, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Logo from "@/pages/home/components/Logo";
import { useAuth } from "@/hooks/useAuth";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import OtpInput from "./components/OtpInput";
import { PASSWORD_MAX, PASSWORD_MIN, passwordProblem } from "@/lib/passwordPolicy";
import Footer from "@/pages/home/components/Footer";

type Mode = "signin" | "signup";

export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready, signIn, signUp, resendSignupCode, verifySignup, requestPasswordReset } = useAuth();

  // Where to land after signing in — e.g. back to the checkout page the
  // visitor was sent here from. A signed-out checkout sends its buyer here in
  // signup mode with the email the credits were paid into.
  const state = location.state as { from?: string; mode?: Mode; email?: string; forgot?: boolean } | null;
  const redirectTo = state?.from || "/profile";
  const [mode, setMode] = useState<Mode>(state?.mode ?? "signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(state?.email ?? "");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set once signup has emailed a code: the address it went to.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  // One character per box: a digit, or a space while that box is empty.
  const [code, setCode] = useState("");
  const codeComplete = /^\d{6}$/.test(code);
  const [resendIn, setResendIn] = useState(0);
  const [forgot, setForgot] = useState(Boolean(state?.forgot));
  const [resetSent, setResetSent] = useState<{ email: string; minutes: number } | null>(null);

  // Counts down to when "Resend code" becomes available again.
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Redirect if already logged in
  useEffect(() => {
    if (ready && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [ready, user, navigate, redirectTo]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setForgot(false);
    setResetSent(null);
    setError(null);
    setNotice(null);
  };

  const backToSignIn = () => {
    setForgot(false);
    setResetSent(null);
    setError(null);
    setNotice(null);
  };

  const handleForgot = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const result = await requestPasswordReset(email);
      setResetSent({ email, minutes: result.expiresInMinutes });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      if (mode === "signup") {
        // Same rule as the server: two or more characters, at least one letter.
        const fullName = name.trim();
        if (fullName.length < 2 || !/\p{L}/u.test(fullName)) {
          setError(t("auth.errors.fullNameRequired"));
          return;
        }
        const problem = passwordProblem(password, { email, fullName });
        if (problem) {
          setError(t(`auth.password.problems.${problem}`, { min: PASSWORD_MIN.user, max: PASSWORD_MAX }));
          return;
        }
        if (!acceptTerms) {
          setError(t("auth.errors.acceptTerms"));
          return;
        }
        const pending = await signUp(email, password, fullName);
        setPendingEmail(pending.email);
        setResendIn(pending.resendAfterSeconds);
        setCode("");
        setNotice(t("auth.verify.codeSent", { email: pending.email }));
        return;
      }
      await signIn(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!pendingEmail) return;
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      await verifySignup(pendingEmail, code.trim());
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || resendIn > 0) return;
    setError(null);
    setNotice(null);

    try {
      const pending = await resendSignupCode(pendingEmail);
      setResendIn(pending.resendAfterSeconds);
      setCode("");
      setNotice(t("auth.verify.newCodeSent", { email: pending.email }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.verify.resendFailed"));
    }
  };

  const changeEmail = () => {
    setPendingEmail(null);
    setCode("");
    setError(null);
    setNotice(null);
  };

  // If auth state is still loading, show a brief spinner so the page doesn't flash
  if (!ready) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full loader-ring"></div>
      </div>
    );
  }

  // If already logged in, the useEffect above will redirect; this is a fallback
  if (user) return null;

  return (
    <>
      <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-primary-950 px-4 md:px-6 py-10">
        <div className="absolute inset-0">
          <div className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full bg-accent-400/20 blur-3xl"></div>
          <div className="absolute bottom-0 -right-24 w-[420px] h-[420px] rounded-full bg-primary-400/20 blur-3xl"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher dark />
          </div>
          <div className="flex justify-center mb-8">
            <Logo dark />
          </div>

          <div className="glass-dark rounded-2xl p-6 md:p-8">
            {pendingEmail ? (
              <>
                <h1 className="font-heading font-bold text-2xl text-background-50">
                  {t("auth.verify.title")}
                </h1>
                <p className="mt-1 text-sm text-background-50/70">
                  <Trans
                    i18nKey="auth.verify.intro"
                    values={{ email: pendingEmail }}
                    components={{ email: <span className="font-medium text-background-50" /> }}
                  />
                </p>

                <form onSubmit={handleVerify} className="mt-6 space-y-4">
                  <div>
                    <p className="block text-sm font-medium text-background-50/80 mb-1.5">
                      {t("auth.verify.codeLabel")}
                    </p>
                    <OtpInput
                      label={t("auth.verify.codeLabel")}
                      value={code}
                      onChange={setCode}
                      disabled={loading}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-300">
                      {error}
                    </div>
                  )}
                  {notice && (
                    <div className="rounded-lg bg-accent-500/10 border border-accent-400/30 px-4 py-3 text-sm text-accent-200">
                      {notice}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !codeComplete}
                    className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
                  >
                    {loading ? t("auth.verify.verifying") : t("auth.verify.submit")}
                  </button>
                </form>

                <div className="mt-5 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={changeEmail}
                    className="text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {t("auth.verify.useDifferentEmail")}
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendIn > 0}
                    className="text-accent-300 hover:text-accent-200 disabled:text-background-50/40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    {resendIn > 0 ? t("auth.verify.resendIn", { seconds: resendIn }) : t("auth.verify.resend")}
                  </button>
                </div>
              </>
            ) : forgot ? (
              <>
                <h1 className="font-heading font-bold text-2xl text-background-50">
                  {t("auth.forgot.title")}
                </h1>
                <p className="mt-1 text-sm text-background-50/70">{t("auth.forgot.subtitle")}</p>

                {resetSent ? (
                  <div className="mt-6 rounded-lg bg-accent-500/10 border border-accent-400/30 px-4 py-3 text-sm text-accent-200">
                    {t("auth.forgot.sent", { email: resetSent.email, minutes: resetSent.minutes })}
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-background-50/80 mb-1.5">
                        {t("auth.fields.email")}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("auth.fields.emailPlaceholder")}
                        autoComplete="email"
                        required
                        className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                      />
                    </div>
                    {error && (
                      <div className="rounded-lg bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-300">
                        {error}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {loading ? t("common.actions.pleaseWait") : t("auth.forgot.submit")}
                    </button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={backToSignIn}
                  className="mt-5 text-sm text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                >
                  {t("auth.forgot.backToSignIn")}
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-50/10 mb-6">
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                      mode === "signin"
                        ? "bg-background-50 text-foreground-950"
                        : "text-background-50/70 hover:text-background-50"
                    }`}
                  >
                    {t("auth.tabs.signIn")}
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMode("signup")}
                    className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                      mode === "signup"
                        ? "bg-background-50 text-foreground-950"
                        : "text-background-50/70 hover:text-background-50"
                    }`}
                  >
                    {t("auth.tabs.signUp")}
                  </button>
                </div>

                <h1 className="font-heading font-bold text-2xl text-background-50">
                  {mode === "signin" ? t("auth.signIn.title") : t("auth.signUp.title")}
                </h1>
                <p className="mt-1 text-sm text-background-50/70">
                  {mode === "signin"
                    ? t("auth.signIn.subtitle")
                    : t("auth.signUp.subtitle")}
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {mode === "signup" && (
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-background-50/80 mb-1.5">
                        {t("auth.fields.fullName")}
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("auth.fields.fullNamePlaceholder")}
                        required
                        minLength={2}
                        maxLength={200}
                        className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-background-50/80 mb-1.5">
                      {t("auth.fields.email")}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("auth.fields.emailPlaceholder")}
                      autoComplete="email"
                      required
                      className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-background-50/80 mb-1.5">
                      {t("auth.fields.password")}
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      required
                      minLength={mode === "signup" ? PASSWORD_MIN.user : undefined}
                      maxLength={mode === "signup" ? PASSWORD_MAX : undefined}
                      aria-describedby={mode === "signup" ? "password-rules" : undefined}
                      className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                    {mode === "signin" && (
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setForgot(true);
                            setError(null);
                            setNotice(null);
                          }}
                          className="text-sm text-accent-300 hover:text-accent-200 transition-colors cursor-pointer"
                        >
                          {t("auth.forgot.link")}
                        </button>
                      </div>
                    )}
                    {mode === "signup" && (
                      <ul id="password-rules" className="mt-2 space-y-1 text-xs" aria-live="polite">
                        {[
                          { key: "length", ok: password.length >= PASSWORD_MIN.user },
                          { key: "letter", ok: /\p{L}/u.test(password) },
                          { key: "number", ok: /\p{N}/u.test(password) },
                          { key: "personal", ok: true },
                        ].map(({ key, ok }) => (
                          <li
                            key={key}
                            className={`flex items-center gap-1.5 ${
                              key === "personal" ? "text-background-50/50" : ok ? "text-accent-200" : "text-background-50/60"
                            }`}
                          >
                            <i
                              className={
                                key === "personal"
                                  ? "ri-information-line"
                                  : ok
                                    ? "ri-checkbox-circle-fill"
                                    : "ri-checkbox-blank-circle-line"
                              }
                              aria-hidden="true"
                            ></i>
                            {t(`auth.password.rules.${key}`, { min: PASSWORD_MIN.user })}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {mode === "signup" && (
                    <label className="flex items-start gap-3 text-xs leading-relaxed text-background-50/80 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        required
                        className="mt-0.5 h-4 w-4 shrink-0 accent-accent-400 cursor-pointer"
                      />
                      <span>
                        <Trans
                          i18nKey="auth.signUp.acceptTerms"
                          components={{
                            terms: <Link to="/terms-of-use" className="text-accent-200 underline" />,
                            privacy: <Link to="/privacy-policy" className="text-accent-200 underline" />,
                          }}
                        />
                      </span>
                    </label>
                  )}

                  {error && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-300">
                      {error}
                    </div>
                  )}
                  {notice && (
                    <div className="rounded-lg bg-accent-500/10 border border-accent-400/30 px-4 py-3 text-sm text-accent-200">
                      {notice}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {loading ? t("common.actions.pleaseWait") : mode === "signin" ? t("auth.signIn.submit") : t("auth.signUp.submit")}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-background-50/60">
            <Link to="/" className="hover:text-background-50 transition-colors">
              ← {t("common.actions.backToHome")}
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}